import type { FleetBoardCard, FleetBoardLane, GetFleetBoardResponse } from '@immo/api-contract';

/** A queue per lane, keyed by the lane's name. */
export type QueueByLane = Readonly<Record<string, ReadonlyArray<FleetBoardCard>>>;

/** `rank` takes exactly one of the two: the ticket goes directly above `before`, or directly below `after`. */
export type QueueNeighbour = { readonly before: string } | { readonly after: string };

/** The queue with the card at `from` moved to index `to`; the same array when nothing moves or an index is out of range. */
export function applyMove<T>(queue: ReadonlyArray<T>, from: number, to: number): ReadonlyArray<T> {
  if (from === to || from < 0 || to < 0 || from >= queue.length || to >= queue.length) return queue;

  const moved = [...queue];
  const [card] = moved.splice(from, 1);
  moved.splice(to, 0, card as T);

  return moved;
}

/**
 * Where the card at `index` of the already-moved queue sits, as the rank request names it: first place goes
 * `before` the card now below it, any other place `after` the card now above it. `null` for a queue of one.
 */
export function neighbourOf(queue: ReadonlyArray<FleetBoardCard>, index: number): QueueNeighbour | null {
  if (index === 0) {
    const below = queue[1];

    return below === undefined ? null : { before: below.identifier };
  }

  const above = queue[index - 1];

  return above === undefined ? null : { after: above.identifier };
}

/**
 * A lane's queue as the page keeps it while it is not the polled one: `override`'s order and cards, but only those the
 * poll still has, and any card the poll gained since put last.
 */
export function mergeQueue(
  polled: ReadonlyArray<FleetBoardCard>,
  override: ReadonlyArray<FleetBoardCard>,
): ReadonlyArray<FleetBoardCard> {
  const polledIds = new Set(polled.map((card) => card.identifier));
  const kept = override.filter((card) => polledIds.has(card.identifier));
  const keptIds = new Set(kept.map((card) => card.identifier));

  return [...kept, ...polled.filter((card) => !keptIds.has(card.identifier))];
}

/** The board with each overridden lane's queue replaced; the same object when nothing is overridden. */
export function applyQueueOverrides(board: GetFleetBoardResponse, overrides: QueueByLane): GetFleetBoardResponse {
  if (board.lanes.every((lane) => overrides[lane.name] === undefined)) return board;

  return {
    ...board,
    lanes: board.lanes.map((lane) => {
      const override = overrides[lane.name];

      return override === undefined ? lane : { ...lane, queue: [...mergeQueue(lane.queue, override)] };
    }),
  };
}

/** A rank is per project and lane: only a lane of exactly one project can be ordered by hand. */
export function rankProjectOf(lane: FleetBoardLane): string | null {
  return lane.projects.length === 1 ? (lane.projects[0]?.project ?? null) : null;
}

export type FoundCard = {
  readonly card: FleetBoardCard;
  readonly lane: string;
  /** 1-based place in the lane's queue, when the card is in it. */
  readonly position: number | null;
};

/** The first lane section holding the ticket: rounds, then stranded, queue, untaken, lane by lane. */
export function findCard(board: GetFleetBoardResponse, identifier: string): FoundCard | null {
  for (const lane of board.lanes) {
    for (const section of [lane.rounds, lane.stranded, lane.queue, lane.untaken]) {
      const at = section.findIndex((card) => card.identifier === identifier);
      const card = section[at];

      if (card !== undefined) {
        return { card, lane: lane.name, position: section === lane.queue ? at + 1 : null };
      }
    }
  }

  return null;
}
